import axios from "./axios";

const normalizeIdString = (value: unknown): string | undefined => {
  if (value == null) return undefined;
  if (typeof value === "string") {
    const t = value.trim();
    return t || undefined;
  }
  if (typeof value === "object" && "_id" in (value as object)) {
    const id = (value as { _id?: unknown })._id;
    if (id == null) return undefined;
    const s = String(id).trim();
    return s || undefined;
  }
  const s = String(value).trim();
  return s || undefined;
};

const resolveScope = (userId?: string, workspaceId?: string) => {
  let resolvedUserId = normalizeIdString(userId);
  let resolvedWorkspaceId = normalizeIdString(workspaceId);

  if (typeof window !== "undefined") {
    if (!resolvedUserId) {
      try {
        const userRaw =
          localStorage.getItem("user") ||
          localStorage.getItem("userData") ||
          sessionStorage.getItem("user") ||
          sessionStorage.getItem("userData");
        const parsedUser = userRaw ? JSON.parse(userRaw) : null;
        const pu = parsedUser as Record<string, unknown> | null;
        const userCandidates = [pu?._id, pu?.id, pu?.userId];
        for (const c of userCandidates) {
          const n = normalizeIdString(c);
          if (n) {
            resolvedUserId = n;
            break;
          }
        }
      } catch {
        // ignore and continue with other fallbacks
      }
    }

    if (!resolvedUserId) {
      try {
        const explicitStorageCandidates = [
          localStorage.getItem("userId"),
          sessionStorage.getItem("userId"),
        ];
        for (const c of explicitStorageCandidates) {
          const n = normalizeIdString(c);
          if (n) {
            resolvedUserId = n;
            break;
          }
        }
      } catch {
        // ignore and continue with owner fallback
      }
    }

    if (!resolvedUserId) {
      try {
        const ownerRaw = localStorage.getItem("owner");
        const parsedOwner = ownerRaw ? JSON.parse(ownerRaw) : null;
        const po = parsedOwner as Record<string, unknown> | null;
        const candidates = [
          po?.userId,
          (po?.userId as { _id?: unknown } | undefined)?._id,
          po?.user,
          (po?.user as { _id?: unknown } | undefined)?._id,
          po?._userId,
          (po?._userId as { _id?: unknown } | undefined)?._id,
        ];
        for (const c of candidates) {
          const n = normalizeIdString(c);
          if (n) {
            resolvedUserId = n;
            break;
          }
        }
      } catch {
        resolvedUserId = undefined;
      }
    }

    if (!resolvedWorkspaceId) {
      try {
        const workspaceRaw = localStorage.getItem("currentWorkSpace");
        const parsedWorkspace = workspaceRaw ? JSON.parse(workspaceRaw) : null;
        resolvedWorkspaceId = normalizeIdString(parsedWorkspace?._id);
      } catch {
        resolvedWorkspaceId = undefined;
      }
    }

    if (!resolvedWorkspaceId) {
      try {
        const ownerRaw = localStorage.getItem("owner");
        const parsedOwner = ownerRaw ? JSON.parse(ownerRaw) : null;
        const po = parsedOwner as { workspaces?: Array<{ _id?: unknown }> } | null;
        resolvedWorkspaceId = normalizeIdString(po?.workspaces?.[0]?._id);
      } catch {
        // ignore
      }
    }
  }

  return { resolvedUserId, resolvedWorkspaceId };
};

/** Resolves user + workspace ids from explicit args and localStorage fallbacks. */
export const resolveDashboardScope = resolveScope;

export const fetchOwner = async (ownerId: string, userId?: string) => {
  try {
    const { resolvedUserId } = resolveScope(userId);
    const response = await axios.post("/owner/getOwner", {
      ownerId,
      ...(resolvedUserId ? { userId: resolvedUserId } : {}),
    });

    return response.data;
  } catch (error) {
    throw new Error("Error fetching owner");
  }
};

export const fetchUserBilling = async (params: {
  userId?: string;
  ownerId?: string;
}) => {
  try {
    const response = await axios.post("/owner/getUserBilling", params);
    return response.data;
  } catch (error) {
    throw new Error("Error fetching user billing");
  }
};

export const fetchUserOwnersWithWorkspaces = async (
  page: number,
  limit: number,
  search = "",
  userId?: string,
  workspaceId?: string,
  ownerId?: string
) => {
  try {
    const { resolvedUserId, resolvedWorkspaceId } = resolveScope(userId, workspaceId);
    if (!resolvedUserId) {
      throw new Error("Missing user context for getUserOwnersWithWorkspaces");
    }

    const response = await axios.post("/owner/getUserOwnersWithWorkspaces", {
      userId: resolvedUserId,
      workspaceId: resolvedWorkspaceId,
      page,
      limit,
      search,
    });

    const payload = response.data || {};
    const owners = Array.isArray(payload.owners) ? payload.owners : [];

    // Keep same shape used by existing LinkedIn accounts UI.
    const accounts = owners.map((owner: Record<string, any>) => ({
      _id: owner.ownerId || owner._id,
      name: owner.name || "",
      email: owner.email || "",
      picture: owner.profileImage || owner.picture || owner.profilePic || "",
      publicIdentifier: owner.publicIdentifier || "",
      ownerEntityUrn: owner.ownerEntityUrn || "",
      dailyQuota: owner.dailyQuota || {},
      dailyUsage: owner.dailyUsage || {},
      importDailyQuota: owner.importDailyQuota || null,
      isLinkedinPremium: Boolean(owner.isLinkedinPremium),
      isDisconnected: Boolean(owner.isDisconnected),
      workspaceIds: owner.workspaceIds || [],
      hasLinkedinToken: Boolean(owner.hasLinkedinToken),
      executionMode:
        String(owner.executionMode || "").toUpperCase() === "EXTENSION"
          ? "EXTENSION"
          : "CLOUD",
      automationStatus: owner.automationStatus || "",
      automationPaused: Boolean(owner.automationPaused),
      hasActiveOrPausedCampaign: Boolean(owner.hasActiveOrPausedCampaign),
      activeOrPausedCampaignCount: Number(owner.activeOrPausedCampaignCount || 0),
      primaryCampaignId: owner.primaryCampaignId
        ? String(owner.primaryCampaignId)
        : "",
      primaryCampaignName: String(owner.primaryCampaignName || "").trim(),
      primaryCampaignStatus: String(owner.primaryCampaignStatus || "").trim(),
      hasActiveRunningCampaign: Boolean(owner.hasActiveRunningCampaign),
      activeCampaignDisplayName: String(
        owner.activeCampaignDisplayName || "No active campaign"
      ).trim(),
    }));

    return {
      ...payload,
      accounts,
      pagination: payload.pagination || {
        page,
        limit,
        total: accounts.length,
        totalPages: 1,
      },
    };
  } catch {
    throw new Error("Error fetching user owners with workspaces");
  }
};

export const disconnectLinkedinSender = async (
  ownerId: string,
  userId?: string,
  workspaceId?: string
) => {
  const { resolvedUserId, resolvedWorkspaceId } = resolveScope(userId, workspaceId);
  if (!ownerId) {
    throw new Error("Missing sender id");
  }
  const response = await axios.post("/owner/disconnectLinkedinSender", {
    ownerId,
    userId: resolvedUserId,
    workspaceId: resolvedWorkspaceId,
  });
  return response.data;
};

export const updateExecutionMode = async (
  ownerId: string,
  executionMode: "CLOUD" | "EXTENSION",
  userId?: string,
  workspaceId?: string
) => {
  const { resolvedUserId, resolvedWorkspaceId } = resolveScope(userId, workspaceId);
  if (!ownerId) {
    throw new Error("Missing sender id");
  }
  const response = await axios.post("/owner/updateExecutionMode", {
    ownerId,
    executionMode,
    userId: resolvedUserId,
    workspaceId: resolvedWorkspaceId,
  });
  return response.data;
};

export const fetchLiveAutomationActions = async (options: {
  ownerId?: string;
  campaignId?: string;
  limit?: number;
}) => {
  const response = await axios.post("/automation/actions/live", {
    ownerId: options.ownerId,
    campaignId: options.campaignId,
    limit: options.limit || 40,
  });
  return response.data;
};

function getDifferenceInDays(date1: Date, date2: Date): number {
  const msInOneDay = 24 * 60 * 60 * 1000; // Number of milliseconds in a day
  const diffInMs = Math.abs(date2.getTime() - date1.getTime()); // Difference in milliseconds
  return Math.floor(diffInMs / msInOneDay); // Convert milliseconds to days
}

export const checkPermission = async (ownerId: string) => {
  try {
    const response = await axios.post("/owner/getOwner", {
      ownerId,
    });

    const data = response.data;
    const planhistory = data?.planhistory;
    if (!Array.isArray(planhistory) || planhistory.length === 0) {
      return false; // no plan yet
    }

    const planHistory = planhistory[planhistory.length - 1];
    const premium = planHistory.isActive;

    if (planHistory.planId == "FREE_PLAN") {
      if (getDifferenceInDays(new Date(planHistory.chargedAt.toString()), new Date()) > 7) return false;
      return true;
    }
    return premium;
  } catch (error) {
    throw new Error("Error fetching owner");
  }
};

export const signup = async (
  email: string,
  password: string,
  profile?: { firstName?: string; lastName?: string; company?: string }
) => {
  if (!email || !password) {
    return {
      success: false,
      msg: "Email and password are required",
    };
  }
  try {
    const response = await axios.post("/owner/signup", {
      email,
      password,
      firstName: profile?.firstName || "",
      lastName: profile?.lastName || "",
      company: profile?.company || "",
    });

    // The backend should return a structure like:
    // { success: true, msg: "Signup successful", ownerId: ... }
    // or { success: false, msg: "...", ... }
    return response.data;
  } catch (error: any) {
    // If the backend returns a response with error details, use it
    if (error.response && error.response.data) {
      return error.response.data;
    }
    // Otherwise, return a generic error
    return {
      success: false,
      msg: "Internal server error",
      error: error.message || "Unknown error",
    };
  }
};

export const signin = async (email: string, password: string) => {
  if (!email || !password) {
    return {
      success: false,
      msg: "Email and password are required",
    };
  }
  try {
    const response = await axios.post("/owner/signin", {
      email,
      password,
    });

    // The backend should return a structure like:
    // { success: true, msg: "Login successful", owner: ... }
    // or { success: false, msg: "...", ... }
    return response.data;
  } catch (error: any) {
    if (error.response && error.response.data) {
      return error.response.data;
    }
    return {
      success: false,
      msg: "Internal server error",
      error: error.message || "Unknown error",
    };
  }
};

export const forgotPassword = async (email: string) => {
  if (!email) {
    return {
      success: false,
      msg: "Email is required",
    };
  }
  try {
    const response = await axios.post("/owner/forgot-password", { email });
    return response.data;
  } catch (error: any) {
    if (error.response && error.response.data) {
      return error.response.data;
    }
    return {
      success: false,
      msg: "Internal server error",
      error: error.message || "Unknown error",
    };
  }
};

export const resetPassword = async (email: string, token: string, password: string) => {
  if (!email || !token || !password) {
    return {
      success: false,
      msg: "Email, token and password are required",
    };
  }
  try {
    const response = await axios.post("/owner/reset-password", { email, token, password });
    return response.data;
  } catch (error: any) {
    if (error.response && error.response.data) {
      return error.response.data;
    }
    return {
      success: false,
      msg: "Internal server error",
      error: error.message || "Unknown error",
    };
  }
};

/** Pre-launch migration: links LinkedIn public identifier → Owner, creates User. Axios throws on HTTP errors. */
export const completeMigrationAccountSetup = async (payload: Record<string, unknown>) => {
  const response = await axios.post("/owner/completeMigrationAccountSetup", payload);
  return response.data as { success?: boolean; msg?: string; userId?: string; ownerId?: string };
};


export const fetchLists = async (
  ownerId: string,
  workspaceId?: string,
  userId?: string
) => {
  try {
    const { resolvedUserId, resolvedWorkspaceId } = resolveScope(
      userId,
      workspaceId
    );
    const response = await axios.post("/prospect/getProspectList", {
      ownerId,
      userId: resolvedUserId,
      workspaceId: resolvedWorkspaceId,
    });

    return response.data;
  } catch (error) {
    console.log(error);
    throw new Error("Error fetching lists");
  }
};

export const retryImportNow = async (payload: {
  listId: string;
  retryId?: string;
  ownerId?: string;
  userId?: string;
  workspaceId?: string;
}) => {
  const { resolvedUserId, resolvedWorkspaceId } = resolveScope(
    payload.userId,
    payload.workspaceId
  );
  const response = await axios.post("/prospect/retryImportNow", {
    listId: payload.listId,
    retryId: payload.retryId,
    ownerId: payload.ownerId,
    userId: resolvedUserId,
    workspaceId: resolvedWorkspaceId,
  });
  return response.data;
};

export const cancelImportAutoRetry = async (payload: {
  listId: string;
  retryId?: string;
  ownerId?: string;
  userId?: string;
  workspaceId?: string;
}) => {
  const { resolvedUserId, resolvedWorkspaceId } = resolveScope(
    payload.userId,
    payload.workspaceId
  );
  const response = await axios.post("/prospect/cancelImportAutoRetry", {
    listId: payload.listId,
    retryId: payload.retryId,
    ownerId: payload.ownerId,
    userId: resolvedUserId,
    workspaceId: resolvedWorkspaceId,
  });
  return response.data;
};

export const fetchImportDailyQuota = async (payload: {
  ownerId: string;
  userId?: string;
  workspaceId?: string;
}) => {
  const { resolvedUserId, resolvedWorkspaceId } = resolveScope(
    payload.userId,
    payload.workspaceId
  );
  const response = await axios.post("/prospect/getImportDailyQuota", {
    ownerId: payload.ownerId,
    userId: resolvedUserId,
    workspaceId: resolvedWorkspaceId,
  });
  return response.data;
};

export const fetchListImportHistory = async (payload: {
  listId: string;
  page?: number;
  limit?: number;
  ownerId?: string;
  userId?: string;
  workspaceId?: string;
}) => {
  const { resolvedUserId, resolvedWorkspaceId } = resolveScope(
    payload.userId,
    payload.workspaceId
  );
  const response = await axios.post("/prospect/getListImportHistory", {
    listId: payload.listId,
    page: payload.page || 1,
    limit: payload.limit || 10,
    ownerId: payload.ownerId,
    userId: resolvedUserId,
    workspaceId: resolvedWorkspaceId,
  });
  return response.data;
};

export const fetchTemplates = async (
  ownerId: string,
  userId?: string,
  workspaceId?: string
) => {
  try {
    const { resolvedUserId, resolvedWorkspaceId } = resolveScope(
      userId,
      workspaceId
    );
    const response = await axios.post("/template/getTemplates", {
      ownerId,
      userId: resolvedUserId,
      workspaceId: resolvedWorkspaceId,
    });

    return response.data;
  } catch (error) {
    console.log(error);
    throw new Error("Error fetching templates");
  }
};

export const updateTemplate = async (payload: {
  templateId: string;
  type?: string;
  name: string;
  content: unknown;
  ownerId?: string;
  userId?: string;
  workspaceId?: string;
}) => {
  const { resolvedUserId, resolvedWorkspaceId } = resolveScope(
    payload.userId,
    payload.workspaceId
  );
  const response = await axios.post("/template/updateTemplate", {
    ...payload,
    userId: resolvedUserId,
    workspaceId: resolvedWorkspaceId,
  });
  return response.data;
};

export const fetchCampaigns = async (
  ownerId: string,
  userId?: string,
  workspaceId?: string
) => {
  try {
    const { resolvedUserId, resolvedWorkspaceId } = resolveScope(
      userId,
      workspaceId
    );
    const response = await axios.post("/campaign/getCampaigns", {
      ownerId,
      userId: resolvedUserId,
      workspaceId: resolvedWorkspaceId,
    });

    return response.data.campaigns;
  } catch (error) {
    throw new Error("Error fetching campaigns");
  }
};

export const fetchCampaignsWithMetrics = async (
  ownerId: string,
  userId: string | undefined,
  workspaceId: string | undefined,
  params: {
    page: number;
    limit: number;
    status?: string;
    search?: string;
  }
) => {
  try {
    const { resolvedUserId, resolvedWorkspaceId } = resolveScope(userId, workspaceId);
    const response = await axios.post("/campaign/getCampaigns", {
      ownerId,
      userId: resolvedUserId,
      workspaceId: resolvedWorkspaceId,
      page: params.page,
      limit: params.limit,
      status: params.status,
      search: params.search,
    });

    return response.data as { campaigns: any[]; totalCount: number };
  } catch (error) {
    throw new Error("Error fetching campaigns");
  }
};

export type EmailCampaignStatsStatus =
  | "delivered"
  | "scheduled"
  | "failed"
  | "not_started";

export type EmailCampaignStatsRow = {
  prospectId: string;
  name: string;
  email: string | null;
  status: EmailCampaignStatsStatus;
  failureReason: string | null;
  sentAt: string | Date | null;
  inCampaign: boolean;
  emailOpened?: boolean;
  emailOpenedAt?: string | Date | null;
};

export const fetchEmailCampaignsForAnalytics = async (
  ownerId: string | undefined,
  userId?: string,
  workspaceId?: string
) => {
  const { resolvedUserId, resolvedWorkspaceId } = resolveScope(userId, workspaceId);
  const response = await axios.post("/campaign/getEmailCampaignStats", {
    ownerId,
    userId: resolvedUserId,
    workspaceId: resolvedWorkspaceId,
  });
  return (response.data?.campaigns || []) as Array<{
    _id: string;
    campaignName: string;
    campaignStatus?: string;
  }>;
};

export const fetchEmailCampaignStats = async (
  ownerId: string | undefined,
  campaignId: string,
  userId?: string,
  workspaceId?: string
) => {
  const { resolvedUserId, resolvedWorkspaceId } = resolveScope(userId, workspaceId);
  const response = await axios.post("/campaign/getEmailCampaignStats", {
    ownerId,
    userId: resolvedUserId,
    workspaceId: resolvedWorkspaceId,
    campaignId,
  });
  return response.data as {
    campaign: { _id: string; campaignName: string; emailStepCount: number };
    rows: EmailCampaignStatsRow[];
  };
};

export const fetchTags = async (
  ownerId: string,
  userId?: string,
  workspaceId?: string
) => {
  try {
    const { resolvedUserId, resolvedWorkspaceId } = resolveScope(
      userId,
      workspaceId
    );
    const response = await axios.post("/tags/getTags", {
      ownerId,
      userId: resolvedUserId,
      workspaceId: resolvedWorkspaceId,
    });

    return response.data;
  } catch (error) {
    throw new Error("Error fetching tags");
  }
};

export const fetchProspects = async (
  ownerId: string,
  listId: string,
  page: number,
  limit: number,
  userId?: string,
  workspaceId?: string,
  options?: {
    tagId?: string;
  }
) => {
  try {
    const { resolvedUserId, resolvedWorkspaceId } = resolveScope(
      userId,
      workspaceId
    );
    const query = new URLSearchParams({
      page: String(page),
      limit: String(limit),
    });
    if (options?.tagId && options.tagId !== "all") {
      query.set("tagId", options.tagId);
    }
    const response = await axios.post(
      `/prospect/getProspectsOfList?${query.toString()}`,
      {
        ownerId,
        userId: resolvedUserId,
        workspaceId: resolvedWorkspaceId,
        listId,
      }
    );

    return response.data;
  } catch (error) {
    throw new Error("Error fetching prospects");
  }
};

export const fetchLeadsDatabase = async (payload: {
  ownerId?: string;
  userId?: string;
  workspaceId?: string;
  page?: number;
  limit?: number;
  search?: string;
  stage?: string;
}) => {
  const { resolvedUserId, resolvedWorkspaceId } = resolveScope(
    payload.userId,
    payload.workspaceId
  );
  const stage = String(payload.stage || "")
    .trim()
    .toLowerCase();
  const query = new URLSearchParams({
    page: String(payload.page || 1),
    limit: String(payload.limit || 10),
  });
  if (payload.search) query.set("search", payload.search);
  if (stage && stage !== "all") query.set("stage", stage);
  const response = await axios.post(
    `/prospect/getLeadsDatabase?${query.toString()}`,
    {
      ownerId: payload.ownerId,
      userId: resolvedUserId,
      workspaceId: resolvedWorkspaceId,
      searchQuery: payload.search || "",
      stage: stage && stage !== "all" ? stage : undefined,
    }
  );
  return response.data;
};

export type GlobalSearchListHit = {
  _id: string;
  listName: string;
};

export type GlobalSearchProspectHit = {
  _id: string;
  name: string;
  company?: string;
  headline?: string;
  imageUrl?: string;
};

export type GlobalSearchCampaignHit = {
  _id: string;
  campaignName: string;
  campaignStatus?: string;
};

export type GlobalSearchSenderHit = {
  _id: string;
  name: string;
  email?: string;
  imageUrl?: string;
};

export type GlobalSearchResult = {
  lists: GlobalSearchListHit[];
  prospects: GlobalSearchProspectHit[];
  campaigns: GlobalSearchCampaignHit[];
  senders: GlobalSearchSenderHit[];
};

export const fetchGlobalSearch = async (payload: {
  ownerId?: string;
  userId?: string;
  workspaceId?: string;
  q: string;
  limit?: number;
}): Promise<GlobalSearchResult> => {
  const { resolvedUserId, resolvedWorkspaceId } = resolveScope(
    payload.userId,
    payload.workspaceId
  );
  const response = await axios.post("/dashboard/globalSearch", {
    ownerId: payload.ownerId,
    userId: resolvedUserId,
    workspaceId: resolvedWorkspaceId,
    q: payload.q,
    limit: payload.limit ?? 5,
  });
  const data = response.data || {};
  return {
    lists: Array.isArray(data.lists) ? data.lists : [],
    prospects: Array.isArray(data.prospects) ? data.prospects : [],
    campaigns: Array.isArray(data.campaigns) ? data.campaigns : [],
    senders: Array.isArray(data.senders) ? data.senders : [],
  };
};

export const fetchErrorLogs = async (payload: {
  ownerId?: string;
  userId?: string;
  workspaceId?: string;
  page?: number;
  limit?: number;
  channel?: "all" | "linkedin" | "email";
  status?: "all" | "failed" | "retrying" | "skipped";
  days?: number | "all";
}) => {
  const { resolvedUserId, resolvedWorkspaceId } = resolveScope(
    payload.userId,
    payload.workspaceId
  );
  const response = await axios.post("/dashboard/getErrorLogs", {
    ownerId: payload.ownerId,
    userId: resolvedUserId,
    workspaceId: resolvedWorkspaceId,
    page: payload.page ?? 1,
    limit: payload.limit ?? 50,
    channel: payload.channel ?? "all",
    status: payload.status ?? "all",
    days: payload.days ?? "all",
  });
  return response.data || { items: [], total: 0, page: 1, limit: 50 };
};

export const fetchOwnerLinkedin = async (
  ownerId: string,
  profileUrn: string
) => {
  try {
    const response = await axios.post("/linkedin/profileView", {
      ownerId,
      profileUrn,
    });

    return response.data;
  } catch (error) {
    throw new Error("Error fetching owner linkedin");
  }
};

export const fetchOwnerConnections = async (ownerId: string) => {
  try {
    const response = await axios.post("/linkedin/getAllConnections", {
      ownerId,
    });

    return response.data.connections;
  } catch (error) {
    console.log(error);
    throw new Error("Error fetching owner connections");
  }
};

export const fetchAllRequests = async (ownerId: string, type: string) => {
  try {
    const response = await axios.post("/linkedin/getAllRequests", {
      ownerId,
      type,
    });

    return response.data;
  } catch (error) {
    console.log(error);
    throw new Error("Error fetching requests");
  }
};

export const deleteAllRequests = async (
  ownerId: string,
  connectionIds: string[]
) => {
  try {
    const response = await axios.post("/linkedin/deleteAllRequests", {
      ownerId,
      connectionIds,
    });
    console.log(response);
    return response;
  } catch (error) {
    console.log(error);
    throw new Error("Error deleting requests");
  }
};

export const fetchTeamMembers = async (
  ownerId: string,
  workspaceId: string
) => {
  try {
    const response = await axios.post("/teams/getTeamMembers", {
      ownerId,
      workspaceId,
    });

    return response.data;
  } catch (error) {
    console.log(error);
    throw new Error("Error fetching team members");
  }
};

export const checkExistingName = async (
  campaignName: string,
  ownerId?: string,
  userId?: string,
  workspaceId?: string
) => {
  try {
    const { resolvedUserId, resolvedWorkspaceId } = resolveScope(
      userId,
      workspaceId
    );
    let res = await axios.post<{ isCampaignNameAvailable: Boolean }>(
      "/campaign/checkForCampaignName",
      {
        campaignName: campaignName,
        ownerId: ownerId,
        userId: resolvedUserId,
        workspaceId: resolvedWorkspaceId,
      }
    );

    if (res.data.isCampaignNameAvailable) {
      return true;
    } else {
      return false;
    }
  } catch (error) {
    console.log(error);
  }
};

export const getRequestsList = async (
  ownerId: string,
  page: number,
  limit: number
) => {
  try {
    const response = await axios.post(
      `/linkedin/getAllRequestsList?page=${page}&limit=${limit}`,
      {
        ownerId,
      }
    );

    return response.data;
  } catch (error) {
    console.log(error);
    throw new Error("Error fetching requests");
  }
};

export type EmailIntegrationStatus = {
  configured: boolean;
  verified: boolean;
  provider: "smtp" | "sendgrid" | null;
  fromName: string;
  fromEmail: string;
  smtpHost: string;
  smtpPort: string;
  smtpUser: string;
  hasSecret: boolean;
  status: "unverified" | "verified" | "failed" | string;
  lastTestAt: string | null;
  lastError: string;
};

const getApiErrorMessage = (error: unknown, fallback: string) => {
  const data = (error as { response?: { data?: { message?: string; msg?: string } } })?.response
    ?.data;
  return String(data?.message || data?.msg || fallback);
};

export const fetchEmailIntegration = async (payload: {
  ownerId?: string;
  userId?: string;
  workspaceId?: string;
}): Promise<EmailIntegrationStatus> => {
  const { resolvedUserId, resolvedWorkspaceId } = resolveScope(
    payload.userId,
    payload.workspaceId
  );
  try {
    const response = await axios.post("/integrations/getEmail", {
      ownerId: payload.ownerId,
      userId: resolvedUserId,
      workspaceId: resolvedWorkspaceId,
    });
    return response.data as EmailIntegrationStatus;
  } catch (error) {
    throw new Error(getApiErrorMessage(error, "Unable to load email integration"));
  }
};

export const saveAndTestEmailIntegration = async (payload: {
  ownerId?: string;
  userId?: string;
  workspaceId?: string;
  provider: "smtp" | "sendgrid";
  fromName: string;
  fromEmail: string;
  smtpHost?: string;
  smtpPort?: string;
  smtpUser?: string;
  smtpPassword?: string;
  sendgridApiKey?: string;
  testTo: string;
}): Promise<{
  ok: boolean;
  verified: boolean;
  message: string;
  integration: EmailIntegrationStatus;
}> => {
  const { resolvedUserId, resolvedWorkspaceId } = resolveScope(
    payload.userId,
    payload.workspaceId
  );
  try {
    const response = await axios.post("/integrations/saveAndTestEmail", {
      ...payload,
      userId: resolvedUserId,
      workspaceId: resolvedWorkspaceId,
    });
    return response.data;
  } catch (error) {
    throw new Error(getApiErrorMessage(error, "Unable to save and test email"));
  }
};

export type DailyReportPreferences = {
  recipientEmails: string[];
  recipientsText: string;
  deliveryTime: string;
  timezone: string;
  deliveryTimeLabel: string;
  subject: string;
  enabled: boolean;
  configured: boolean;
};

export type DailyReportSummary = {
  connectionRequestsSent: number;
  repliesReceived: number;
  interestedProspects: number;
  campaignCount: number;
};

export type DailyReportHistoryItem = {
  _id: string;
  date: string;
  summary: string;
  subject?: string;
  connectionRequestsSent?: number;
  repliesReceived?: number;
  interestedProspects?: number;
};

export type DailyReportExportItem = {
  date: string;
  summary: string;
  subject?: string;
  connectionRequestsSent: number;
  repliesReceived: number;
  interestedProspects: number;
};

export type DailyReportHistoryResponse = {
  items: DailyReportHistoryItem[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
};

export type DailyReportHistoryEmail = {
  _id: string;
  subject: string;
  date: string;
  summary: string;
  html: string;
};

export type DailyReportPageData = {
  emailChannel: EmailIntegrationStatus;
  preferences: DailyReportPreferences;
  summary: DailyReportSummary;
  lastSentAt: string | null;
  canSendReports: boolean;
};

export const fetchDailyReportPage = async (payload: {
  ownerId?: string;
  userId?: string;
  workspaceId?: string;
}): Promise<DailyReportPageData> => {
  const { resolvedUserId, resolvedWorkspaceId } = resolveScope(
    payload.userId,
    payload.workspaceId
  );
  try {
    const response = await axios.post("/dashboard/getDailyReportPage", {
      ownerId: payload.ownerId,
      userId: resolvedUserId,
      workspaceId: resolvedWorkspaceId,
    });
    return response.data as DailyReportPageData;
  } catch (error) {
    throw new Error(getApiErrorMessage(error, "Unable to load daily report page"));
  }
};

export const saveDailyReportPreferences = async (payload: {
  ownerId?: string;
  userId?: string;
  workspaceId?: string;
  recipientsText: string;
  deliveryTimeLabel: string;
  timezone?: string;
  deliveryTime?: string;
  subject: string;
}): Promise<{ ok: boolean; preferences: DailyReportPreferences }> => {
  const { resolvedUserId, resolvedWorkspaceId } = resolveScope(
    payload.userId,
    payload.workspaceId
  );
  try {
    const response = await axios.post("/dashboard/saveDailyReportPreferences", {
      ownerId: payload.ownerId,
      userId: resolvedUserId,
      workspaceId: resolvedWorkspaceId,
      recipientsText: payload.recipientsText,
      deliveryTimeLabel: payload.deliveryTimeLabel,
      timezone: payload.timezone,
      deliveryTime: payload.deliveryTime,
      subject: payload.subject,
    });
    return response.data;
  } catch (error) {
    throw new Error(getApiErrorMessage(error, "Unable to save daily report preferences"));
  }
};

export const previewDailyReportEmail = async (payload: {
  ownerId?: string;
  userId?: string;
  workspaceId?: string;
}): Promise<{ ok: boolean; message: string }> => {
  const { resolvedUserId, resolvedWorkspaceId } = resolveScope(
    payload.userId,
    payload.workspaceId
  );
  try {
    const response = await axios.post("/dashboard/previewDailyReportEmail", {
      ownerId: payload.ownerId,
      userId: resolvedUserId,
      workspaceId: resolvedWorkspaceId,
    });
    return response.data;
  } catch (error) {
    throw new Error(getApiErrorMessage(error, "Unable to send preview email"));
  }
};

export const fetchDailyReportHistory = async (payload: {
  ownerId?: string;
  userId?: string;
  workspaceId?: string;
  page?: number;
  limit?: number;
}): Promise<DailyReportHistoryResponse> => {
  const { resolvedUserId, resolvedWorkspaceId } = resolveScope(
    payload.userId,
    payload.workspaceId
  );
  try {
    const response = await axios.post("/dashboard/getDailyReportHistory", {
      ownerId: payload.ownerId,
      userId: resolvedUserId,
      workspaceId: resolvedWorkspaceId,
      page: payload.page ?? 1,
      limit: payload.limit ?? 10,
    });
    return response.data as DailyReportHistoryResponse;
  } catch (error) {
    throw new Error(getApiErrorMessage(error, "Unable to load previous email reports"));
  }
};

export const fetchDailyReportHistoryEmail = async (payload: {
  ownerId?: string;
  userId?: string;
  workspaceId?: string;
  historyId: string;
}): Promise<DailyReportHistoryEmail> => {
  const { resolvedUserId, resolvedWorkspaceId } = resolveScope(
    payload.userId,
    payload.workspaceId
  );
  try {
    const response = await axios.post("/dashboard/getDailyReportHistoryEmail", {
      ownerId: payload.ownerId,
      userId: resolvedUserId,
      workspaceId: resolvedWorkspaceId,
      historyId: payload.historyId,
    });
    return response.data as DailyReportHistoryEmail;
  } catch (error) {
    throw new Error(getApiErrorMessage(error, "Unable to load report email"));
  }
};

export const downloadDailyReportHistory = async (payload: {
  ownerId?: string;
  userId?: string;
  workspaceId?: string;
}): Promise<{ ok: boolean; filename: string; items: DailyReportExportItem[]; count: number }> => {
  const { resolvedUserId, resolvedWorkspaceId } = resolveScope(
    payload.userId,
    payload.workspaceId
  );
  try {
    const response = await axios.post("/dashboard/downloadDailyReportHistory", {
      ownerId: payload.ownerId,
      userId: resolvedUserId,
      workspaceId: resolvedWorkspaceId,
    });
    return response.data;
  } catch (error) {
    throw new Error(getApiErrorMessage(error, "Unable to download previous email reports"));
  }
};

export const disconnectEmailIntegration = async (payload: {
  ownerId?: string;
  userId?: string;
  workspaceId?: string;
}): Promise<EmailIntegrationStatus> => {
  const { resolvedUserId, resolvedWorkspaceId } = resolveScope(
    payload.userId,
    payload.workspaceId
  );
  try {
    const response = await axios.post("/integrations/disconnectEmail", {
      ownerId: payload.ownerId,
      userId: resolvedUserId,
      workspaceId: resolvedWorkspaceId,
    });
    return response.data as EmailIntegrationStatus;
  } catch (error) {
    throw new Error(getApiErrorMessage(error, "Unable to disconnect email"));
  }
};

export const fetchTeamsData = async (ownerId: string) => {
  try {
    const response = await axios.post("/teams/getTeamsData", {
      ownerId,
    });

    return response.data;
  } catch (error) {
    console.log(error);
    throw new Error("Error fetching teams data");
  }
};

export {
  createMergeTokenYupTest,
  findUnsupportedMergeTokens,
  formatSupportedMergeTokensList,
  getMergeTokenValidationError,
  INVITATION_CUSTOM_MESSAGE_MAX_LENGTH,
  INVITATION_MERGE_TOKEN_RESERVE_CHARS,
  INVITATION_TEMPLATE_BASE_MAX_LENGTH,
  INVITE_MERGE_TOKEN_FIELDS,
  INVITE_MERGE_TOKEN_KEYS,
  countInviteMergeTokenUsages,
  getInvitationTemplateLengthError,
  getInvitationTemplateMaxLength,
  MESSAGE_MERGE_TOKEN_FIELDS,
  MESSAGE_MERGE_TOKEN_KEYS,
  normalizeSingleBraceMergeTokens,
} from "./mergeTokens";
export type { InviteMergeTokenKey, MergeTokenField, MessageMergeTokenKey } from "./mergeTokens";